import type { FileWithLatestFileVersion } from "schemas/generated/file";

import React from "react";

import { Button } from "@replo/design-system/components/button/Button";
import { Skeleton } from "@replo/design-system/components/skeleton/Skeleton";
import { Tooltip } from "@replo/design-system/components/tooltip/Tooltip";
import { DebugMenu } from "@v2/features/debug/DebugMenu";
import usePublishedSiteUrls from "@v2/features/home/hooks/usePublishedSiteUrls";
import { useModifiersStoreActions } from "@v2/features/modifiers/store/ModifiersStoreContext";
import PageSettingsMenu from "@v2/features/page-renderer/components/navbar/PageSettingsMenu";
import { PublishButton } from "@v2/features/page-renderer/components/navbar/PublishButton";
import { ShareButton } from "@v2/features/page-renderer/components/navbar/ShareButton/ShareButton";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { useEditorStoreActions, usePanelView } from "@v2/store/editorStore";
import { History } from "lucide-react";
import { useSearchParams } from "react-router-dom";

import PageNameWithDropdown from "./navbar/PageNameWithDropdown";
import ResponsiveControls from "./navbar/ResponsiveControls";

type Props = {
  page: FileWithLatestFileVersion | undefined;
  isLoading?: boolean;
};

function PageRenderControls({ page, isLoading = false }: Props) {
  const projectId = useCurrentProjectId();
  const [isPageSelectionPopoverOpen, setIsPageSelectionPopoverOpen] =
    React.useState(false);

  const panelView = usePanelView();
  const { setPanelView } = useEditorStoreActions();
  const modifierActions = useModifiersStoreActions();
  const [_searchParams, setSearchParams] = useSearchParams();

  const publishedSiteUrls = usePublishedSiteUrls({
    pageRoutesToUseForPreview: page?.pageRoute ? [page.pageRoute] : [],
  });

  const publishedSiteUrl = publishedSiteUrls[0];
  const pageSelector = (
    <>
      {isLoading || !page ? (
        <Skeleton className="h-8 w-32" />
      ) : (
        <PageNameWithDropdown
          page={page}
          projectId={projectId ?? ""}
          isPageSelectionPopoverOpen={isPageSelectionPopoverOpen}
          setIsPageSelectionPopoverOpen={setIsPageSelectionPopoverOpen}
          onPageSelect={(selectedPage) => {
            setSearchParams((prev) => {
              const newParams = new URLSearchParams(prev);
              newParams.set("pageId", selectedPage.id);
              return newParams;
            });
            setIsPageSelectionPopoverOpen(false);
          }}
        />
      )}
    </>
  );

  const historyButton = (
    <Tooltip content="Version History" triggerAsChild>
      <Button
        icon
        variant="tertiary"
        size="base"
        UNSAFE_className={
          panelView === "history"
            ? "bg-sky-soft text-sky border-sky-border"
            : ""
        }
        onClick={() => {
          if (panelView !== "history") {
            modifierActions.setSelectedElementDesignations(null);
            modifierActions.setDisplayMode("interactive");
          } else {
            modifierActions.setDisplayMode("select");
          }
          setPanelView(panelView === "history" ? "chat" : "history");
        }}
      >
        <History />
      </Button>
    </Tooltip>
  );

  const publishButton =
    isLoading || !page || !publishedSiteUrl ? (
      <Skeleton className="h-8 w-16" />
    ) : (
      <PublishButton
        routeType="page"
        publishedSiteUrl={publishedSiteUrl}
        fileIdsToPublish={[page.id]}
        isViewableLive={Boolean(page.lastPublishedFileVersionId)}
      />
    );

  const shareButton =
    isLoading || !page ? <Skeleton className="h-8 w-16" /> : <ShareButton />;

  return (
    <>
      <div className="hidden md:flex border-b-0.5 border-border w-full px-4 py-3 max-h-16 flex-row items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          {pageSelector}
          {historyButton}
          <PageSettingsMenu />
          <DebugMenu />
        </div>
        <div className="flex items-center gap-2">
          <ResponsiveControls />
          {shareButton}
          {publishButton}
        </div>
      </div>

      <div className="md:hidden border-b-0.5 border-border w-full px-4 py-3 flex flex-col gap-3">
        <div className="flex items-center w-full gap-2 justify-between">
          <div className="flex-1 min-w-0">{pageSelector}</div>
          <div className="flex items-center gap-2">
            {shareButton}
            {publishButton}
          </div>
        </div>
        <div className="flex w-full gap-2 justify-between">
          {historyButton}
          <PageSettingsMenu />
          <ResponsiveControls />
        </div>
      </div>
    </>
  );
}

export default PageRenderControls;
